import { createContext, useContext, useReducer, type Dispatch, type ReactNode } from "react";
import { todayISO, applyNumpadKey, uuid4, CURRENCY_DECIMALS } from "@/lib/utils";
import type { Expense } from "@/api/types";

export type Screen = "main" | "history" | "stats" | "edit" | "note";
export type ModalName = "currency" | "date" | "account" | "menu" | null;

export interface State {
    amount: string;            // строка суммы (numpad)
    currency: string;
    accountId: string | null;  // null = «без счёта»
    date: string;
    note: string;
    categoryId: string | null; // выбранная категория (для edit; на главной категорию задаёт тап-создание)
    editingId: string | null;  // id редактируемой траты (null = создание новой)
    draftId: string;           // UUID будущей записи: один на драфт (MA-01/SPEC-042) — ретрай и
                               // двойной тап шлют тот же id, INSERT OR IGNORE дедуплицирует
    returnScreen: Screen;      // MA-04 (SPEC-048): откуда пришли в edit (main/history/stats) — туда и вернёмся
    screen: Screen;
    modal: ModalName;
}

export type Action =
    | { t: "key"; k: string }            // numpad-клавиша
    | { t: "currency"; v: string }
    | { t: "account"; v: string | null; ccy?: string | null }   // ccy: валюта счёта для auto-bind (SPEC-032)
    | { t: "date"; v: string }
    | { t: "note"; v: string }
    | { t: "category"; v: string | null }
    | { t: "resetDraft" }
    | { t: "loadEdit"; e: Expense }
    | { t: "screen"; v: Screen }
    | { t: "modal"; v: ModalName };

const ACC_KEY = "mini.lastAccount";
const ACC_CCY_KEY = "mini.lastAccountCcy";   // SPEC-032: валюта липкого счёта — чтобы дефолтный драфт был консистентным

type Draft = Pick<State, "amount" | "currency" | "accountId" | "date" | "note" | "categoryId" | "editingId" | "draftId">;

function freshDraft(): Draft {
    return {
        amount: "0",
        draftId: uuid4(),
        // SPEC-032: дефолт валюты = валюта липкого счёта (а не захардкоженный RSD), чтобы новый
        // драфт сразу был согласован со счётом. Если счёта нет — fallback RSD. Финальная сверка
        // с реальным справочником — reconcile-эффект в MainScreen (чинит легаси-localStorage).
        currency: localStorage.getItem(ACC_CCY_KEY) || "RSD",
        accountId: localStorage.getItem(ACC_KEY) || null,
        date: todayISO(),
        note: "",
        categoryId: null,
        editingId: null,
    };
}

function init(): State {
    // MA-13 (SPEC-048): baseCurrency мёртв (SPEC-036 NG1, всегда EUR) — вычищаем возможное
    // легаси-значение из localStorage, чтобы оно никогда не мислейбило DayTotal.
    localStorage.removeItem("mini.baseCurrency");
    return { ...freshDraft(), returnScreen: "main", screen: "main", modal: null };
}

function reducer(s: State, a: Action): State {
    switch (a.t) {
        // MA-07 (SPEC-048): decimal-cap по валюте — BTC/ETH получают свои 8/6 знаков,
        // RSD (0 знаков) вовсе не принимает точку.
        case "key": return { ...s, amount: applyNumpadKey(s.amount, a.k, CURRENCY_DECIMALS[s.currency] ?? 2) };
        case "currency": return { ...s, currency: a.v, modal: null };
        case "account":
            // SPEC-032 auto-bind: выбор счёта сразу ставит его валюту (трата со счётом обязана
            // быть в валюте ведра). «Без счёта» (v=null) валюту не трогает — она становится свободной.
            if (a.v) {
                localStorage.setItem(ACC_KEY, a.v);
                if (a.ccy) localStorage.setItem(ACC_CCY_KEY, a.ccy);
            } else {
                localStorage.removeItem(ACC_KEY);
            }
            return { ...s, accountId: a.v, currency: a.v && a.ccy ? a.ccy : s.currency, modal: null };
        case "date": return { ...s, date: a.v, modal: null };
        case "note": return { ...s, note: a.v };
        case "category": return { ...s, categoryId: a.v };
        case "resetDraft": return { ...s, ...freshDraft() };
        case "loadEdit": {
            const e = a.e;
            return {
                ...s,
                amount: String(e.amount), currency: e.currency, accountId: e.account_id,
                date: e.date, note: e.note ?? "", categoryId: e.category_id, editingId: e.id,
                // MA-04 (SPEC-048): запоминаем исходный экран — «Назад»/сохранение/удаление
                // возвращают в контекст (История/Статистика), а не всегда на главный.
                returnScreen: s.screen === "edit" ? s.returnScreen : s.screen,
                screen: "edit", modal: null,
            };
        }
        case "screen": return { ...s, screen: a.v, modal: null };
        case "modal": return { ...s, modal: a.v };
    }
}

const Ctx = createContext<{ s: State; d: Dispatch<Action> } | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
    const [s, d] = useReducer(reducer, undefined, init);
    return <Ctx.Provider value={{ s, d }}>{children}</Ctx.Provider>;
}

export function useApp() {
    const c = useContext(Ctx);
    if (!c) throw new Error("AppProvider missing");
    return c;
}

export function amountValue(s: State): number {
    return parseFloat(s.amount) || 0;
}
